<script lang="ts">
  import type { Route, Column, SortDirection } from '../utils/routing.service.js';

  import Icon from './Icon.svelte';
  import PaginationBar from './PaginationBar.svelte';
  import Button from './Button.svelte';

  export let routings: Route[] = [];
  export let columns: Column[];
  export let pageSize: number;
  export let totalCount: number;
  export let sortBy: string | undefined = undefined;
  export let sortDirection: SortDirection = 'ASC';
  export let currentPageIndex: number;

  export let onDeleteClick: (routing: Route) => void = () => {};
  export let onEditClick: (routing: Route) => void = () => {};

  const isColumnSortable = (column: Column) => column.sortable && column.field;

  const isValidField = (route: Route, key?: string): key is keyof Route => {
    return !!key && key in route;
  };

  const getFieldContent = (route: Route, column: Column) => {
    if (!isValidField(route, column.field)) {
      return '';
    }

    const value = route[column.field];

    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein';
    }

    if (!value) {
      return '';
    }

    return column.formatter ? column.formatter(value) : value;
  };

  const onTableHeadClick = (column: Column) => {
    if (isColumnSortable(column)) {
      sortBy = column.field;

      if (sortBy === column.field) {
        sortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
      } else {
        sortDirection = 'DESC';
      }
    }
  };
</script>

<div class="table-wrapper">
  <table class="table">
    <thead class="table-head">
      {#each columns as column}
        <th
          class="table-head-cell {column.field === sortBy ? sortDirection.toLowerCase() : ''}"
          class:sortable={isColumnSortable(column)}
          on:click={() => onTableHeadClick(column)}
        >
          <span>
            {column.label}

            {#if isColumnSortable(column)}
              <span class="sort-arrow">
                <Icon variant="arrowUp" />
              </span>
            {/if}
          </span>
        </th>
      {/each}
      <th class="table-head-cell" />
    </thead>
    <tbody class="table-body">
      {#each routings as routing}
        <tr>
          {#each columns as column}
            <td>{@html getFieldContent(routing, column)}</td>
          {/each}

          <td>
            <div class="button-group">
              <Button title="editieren" size="extra-small" on:click={() => onEditClick(routing)}>
                <Icon variant="edit" />
              </Button>
              <Button
                title="löschen"
                size="extra-small"
                variant="outlineRed"
                on:click={() => onDeleteClick(routing)}
              >
                <Icon variant="delete" />
              </Button>
            </div>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <PaginationBar
    {pageSize}
    totalElements={totalCount}
    bind:currentPageIndex
  />
</div>

<style lang="scss">
  @use '../styles/colors';
  @use '../styles/spacing';
  @use '../styles/font-sizes';

  .table-wrapper {
    display: block;
    border: 1px solid colors.$black10;
    border-radius: 4px;
  }

  .table {
    width: 100%;
    text-align: left;
    border-collapse: collapse;
  }

  .table-head {
    background-color: colors.$whiteHover;
    border-bottom: 1px solid colors.$black10;
  }

  .table-head-cell {
    padding: spacing.$xs;
    font-size: font-sizes.$text-s;
    color: colors.$black60;
    line-height: 1.42;
    vertical-align: middle;

    &:first-child {
      padding-left: spacing.$xs;
    }

    &:last-child {
      padding-right: spacing.$xs;
    }

    &.sortable:hover {
      cursor: pointer;

      .sort-arrow {
        visibility: visible;
      }
    }

    & > span {
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    .sort-arrow {
      visibility: hidden;
      transition: transform 250ms ease-in;
      transform: rotateX(0deg);
    }

    &.asc,
    &.desc {
      .sort-arrow {
        visibility: visible;
      }
    }

    &.asc {
      .sort-arrow {
        transform: rotateX(180deg);
      }
    }
  }

  .table-body {
    tr:not(:last-of-type) {
      border-bottom: 1px solid colors.$black10;
    }

    tr:hover {
      background-color: colors.$black04;
    }
    td {
      font-size: font-sizes.$text-m;
      line-height: 1.25;
      padding: spacing.$s spacing.$xs;
      overflow-wrap: anywhere;
      &:first-child {
        padding-left: spacing.$s;
      }
      &:last-child {
        padding-right: spacing.$s;
        display: flex;
        justify-content: flex-end;
      }
    }
  }

  .button-group {
    display: flex;
    flex-wrap: nowrap;
    gap: spacing.$xxxs;
  }
</style>
